import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useRef } from 'react';

const ProductPage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Global scroll progress for the moving graphic
  const { scrollYProgress: globalScrollProgress } = useScroll();
  
  const y = useTransform(scrollYProgress, [0, 1], [300, -300]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  
  // Moving graphic progress
  const graphicProgress = useSpring(globalScrollProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'oklch(0.23 0.002 264)' }}>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="text-center text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-5xl lg:text-7xl font-bold mb-6 font-slim"
          >
            Our Products
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl lg:text-2xl text-gray-300 max-w-2xl mx-auto font-slim"
          >
            Discover our innovative solutions designed to transform your business
          </motion.p>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Scroll Timeline Container */}
      <div ref={containerRef} className="relative">
        {/* Timeline Line with moving dot */}
        <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-yellow-400 via-cyan-400 to-green-400"></div>
        

        {/* Timeline Items */}
        <div className="space-y-32 py-32">
          {/* Item 1 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">01</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">AI-Receptionist</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    An AI Receptionist is a digital assistant that automatically handles customer interactions — such as answering queries, scheduling appointments, taking messages, and routing calls/emails — without human intervention.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Instant Response</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Smart Routing</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">24/7 Support</span>
                  </div>
                </motion.div>

                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Blue Theme */}
                        <motion.circle cx="20" cy="20" r="2" fill="#3b82f6" opacity="0.8" 
                          animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="80" cy="20" r="2" fill="#8b5cf6" opacity="0.8"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
                        <motion.circle cx="20" cy="80" r="2" fill="#06b6d4" opacity="0.8"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        <motion.circle cx="80" cy="80" r="2" fill="#3b82f6" opacity="0.8"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="15" fill="url(#gradient1)"
                          animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="8" fill="url(#gradient2)"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
                        <motion.circle cx="50" cy="50" r="3" fill="#ffffff"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 1, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        
                        {/* Connections */}
                        <motion.line x1="20" y1="20" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.5" opacity="0.6"
                          animate={{ opacity: [0.6, 1, 0.6], strokeWidth: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="80" y1="20" x2="50" y2="50" stroke="#8b5cf6" strokeWidth="0.5" opacity="0.6"
                          animate={{ opacity: [0.6, 1, 0.6], strokeWidth: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
                        <motion.line x1="20" y1="80" x2="50" y2="50" stroke="#06b6d4" strokeWidth="0.5" opacity="0.6"
                          animate={{ opacity: [0.6, 1, 0.6], strokeWidth: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        <motion.line x1="80" y1="80" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.5" opacity="0.6"
                          animate={{ opacity: [0.6, 1, 0.6], strokeWidth: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} />
                        
                        <defs>
                          <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#06b6d4" />
                          </linearGradient>
                          <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="50%" stopColor="#06b6d4" />
                            <stop offset="100%" stopColor="#3b82f6" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Item 2 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative lg:order-1"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Green Theme */}
                        <motion.circle cx="25" cy="25" r="2" fill="#10b981" opacity="0.8" 
                          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="75" cy="25" r="2" fill="#06b6d4" opacity="0.8"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} />
                        <motion.circle cx="25" cy="75" r="2" fill="#059669" opacity="0.8"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }} />
                        <motion.circle cx="75" cy="75" r="2" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.8 }} />
                        
                        {/* Secondary Nodes */}
                        <motion.circle cx="50" cy="15" r="1.5" fill="#06b6d4" opacity="0.6"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.3 }} />
                        <motion.circle cx="15" cy="50" r="1.5" fill="#10b981" opacity="0.6"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.9 }} />
                        <motion.circle cx="85" cy="50" r="1.5" fill="#059669" opacity="0.6"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} />
                        <motion.circle cx="50" cy="85" r="1.5" fill="#06b6d4" opacity="0.6"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2.1 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="18" fill="url(#gradient3)"
                          animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="10" fill="url(#gradient4)"
                          animate={{ scale: [1, 1.25, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }} />
                        <motion.circle cx="50" cy="50" r="4" fill="#ffffff"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }} />
                        
                        {/* Connections */}
                        <motion.line x1="25" y1="25" x2="50" y2="50" stroke="#10b981" strokeWidth="0.6" opacity="0.7"
                          animate={{ opacity: [0.7, 1, 0.7], strokeWidth: [0.6, 1.2, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="75" y1="25" x2="50" y2="50" stroke="#06b6d4" strokeWidth="0.6" opacity="0.7"
                          animate={{ opacity: [0.7, 1, 0.7], strokeWidth: [0.6, 1.2, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} />
                        <motion.line x1="25" y1="75" x2="50" y2="50" stroke="#059669" strokeWidth="0.6" opacity="0.7"
                          animate={{ opacity: [0.7, 1, 0.7], strokeWidth: [0.6, 1.2, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }} />
                        <motion.line x1="75" y1="75" x2="50" y2="50" stroke="#10b981" strokeWidth="0.6" opacity="0.7"
                          animate={{ opacity: [0.7, 1, 0.7], strokeWidth: [0.6, 1.2, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.8 }} />
                        
                        <defs>
                          <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="50%" stopColor="#06b6d4" />
                            <stop offset="100%" stopColor="#059669" />
                          </linearGradient>
                          <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#06b6d4" />
                            <stop offset="50%" stopColor="#059669" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6 lg:order-2"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">02</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">Lead Generation Automation</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    Lead generation automation is the process of using AI + automation tools to automatically find, capture, qualify, and nurture potential customers (leads) without manual effort.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Targeted Outreach</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Auto Qualification</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">Data Insights</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Item 3 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">03</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">Social Media Automation</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    Social media posting automation means setting up a workflow that automatically publishes or schedules posts across multiple platforms (like Instagram, LinkedIn, Facebook, X/Twitter, or TikTok) without manually logging into each one.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Schedule Posts</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Trend Tracking</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">Engagement Boost</span>
                  </div>
                </motion.div>

                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Purple Theme */}
                        <motion.circle cx="30" cy="30" r="2.5" fill="#8b5cf6" opacity="0.9" 
                          animate={{ scale: [1, 1.4, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="70" cy="30" r="2.5" fill="#ec4899" opacity="0.9"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.7 }} />
                        <motion.circle cx="30" cy="70" r="2.5" fill="#a855f7" opacity="0.9"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.4 }} />
                        <motion.circle cx="70" cy="70" r="2.5" fill="#8b5cf6" opacity="0.9"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2.1 }} />
                        
                        {/* Tertiary Nodes */}
                        <motion.circle cx="50" cy="10" r="1.8" fill="#ec4899" opacity="0.7"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} />
                        <motion.circle cx="10" cy="50" r="1.8" fill="#8b5cf6" opacity="0.7"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.9 }} />
                        <motion.circle cx="90" cy="50" r="1.8" fill="#a855f7" opacity="0.7"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.6 }} />
                        <motion.circle cx="50" cy="90" r="1.8" fill="#ec4899" opacity="0.7"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2.3 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="20" fill="url(#gradient5)"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="12" fill="url(#gradient6)"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }} />
                        <motion.circle cx="50" cy="50" r="5" fill="#ffffff"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} />
                        
                        {/* Connections */}
                        <motion.line x1="30" y1="30" x2="50" y2="50" stroke="#8b5cf6" strokeWidth="0.7" opacity="0.8"
                          animate={{ opacity: [0.8, 1, 0.8], strokeWidth: [0.7, 1.4, 0.7] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="70" y1="30" x2="50" y2="50" stroke="#ec4899" strokeWidth="0.7" opacity="0.8"
                          animate={{ opacity: [0.8, 1, 0.8], strokeWidth: [0.7, 1.4, 0.7] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.7 }} />
                        <motion.line x1="30" y1="70" x2="50" y2="50" stroke="#a855f7" strokeWidth="0.7" opacity="0.8"
                          animate={{ opacity: [0.8, 1, 0.8], strokeWidth: [0.7, 1.4, 0.7] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.4 }} />
                        <motion.line x1="70" y1="70" x2="50" y2="50" stroke="#8b5cf6" strokeWidth="0.7" opacity="0.8"
                          animate={{ opacity: [0.8, 1, 0.8], strokeWidth: [0.7, 1.4, 0.7] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2.1 }} />
                        
                        <defs>
                          <linearGradient id="gradient5" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="50%" stopColor="#ec4899" />
                            <stop offset="100%" stopColor="#a855f7" />
                          </linearGradient>
                          <linearGradient id="gradient6" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ec4899" />
                            <stop offset="50%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#8b5cf6" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Item 4 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative lg:order-1"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Orange Theme */}
                        <motion.circle cx="22" cy="22" r="3" fill="#f97316" opacity="0.9" 
                          animate={{ scale: [1, 1.5, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="78" cy="22" r="3" fill="#dc2626" opacity="0.9"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }} />
                        <motion.circle cx="22" cy="78" r="3" fill="#ea580c" opacity="0.9"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.6 }} />
                        <motion.circle cx="78" cy="78" r="3" fill="#f97316" opacity="0.9"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2.4 }} />
                        
                        {/* Additional Nodes */}
                        <motion.circle cx="50" cy="8" r="2" fill="#dc2626" opacity="0.8"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.1 }} />
                        <motion.circle cx="8" cy="50" r="2" fill="#f97316" opacity="0.8"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        <motion.circle cx="92" cy="50" r="2" fill="#ea580c" opacity="0.8"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.8 }} />
                        <motion.circle cx="50" cy="92" r="2" fill="#dc2626" opacity="0.8"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2.6 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="22" fill="url(#gradient7)"
                          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="14" fill="url(#gradient8)"
                          animate={{ scale: [1, 1.35, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} />
                        <motion.circle cx="50" cy="50" r="6" fill="#ffffff"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }} />
                        
                        {/* Connections */}
                        <motion.line x1="22" y1="22" x2="50" y2="50" stroke="#f97316" strokeWidth="0.8" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.8, 1.6, 0.8] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="78" y1="22" x2="50" y2="50" stroke="#dc2626" strokeWidth="0.8" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.8, 1.6, 0.8] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }} />
                        <motion.line x1="22" y1="78" x2="50" y2="50" stroke="#ea580c" strokeWidth="0.8" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.8, 1.6, 0.8] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.6 }} />
                        <motion.line x1="78" y1="78" x2="50" y2="50" stroke="#f97316" strokeWidth="0.8" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.8, 1.6, 0.8] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2.4 }} />
                        
                        <defs>
                          <linearGradient id="gradient7" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f97316" />
                            <stop offset="50%" stopColor="#dc2626" />
                            <stop offset="100%" stopColor="#ea580c" />
                          </linearGradient>
                          <linearGradient id="gradient8" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#dc2626" />
                            <stop offset="50%" stopColor="#ea580c" />
                            <stop offset="100%" stopColor="#f97316" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6 lg:order-2"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">04</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">Inbound Calling Agent</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    An Inbound AI Calling Agent automatically answers incoming phone calls from customers, understands what they're saying using speech recognition (voice-to-text), processes their intent with AI (like GPT-5), and responds naturally with text-to-speech just like a human receptionist or support agent.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Voice Recognition</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Call Handling</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">Real-Time Logging</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Item 5 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">05</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">Appointment Setter</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    An AI Appointment Setter Agent is an intelligent, automated virtual assistant that handles the entire appointment scheduling process for a business from the first client inquiry to confirmation and reminders without any human intervention.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Smart Scheduling</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Calendar Sync</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">Reminder System</span>
                  </div>
                </motion.div>

                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Indigo Theme */}
                        <motion.circle cx="18" cy="18" r="3.5" fill="#6366f1" opacity="0.9" 
                          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="82" cy="18" r="3.5" fill="#3b82f6" opacity="0.9"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        <motion.circle cx="18" cy="82" r="3.5" fill="#4f46e5" opacity="0.9"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }} />
                        <motion.circle cx="82" cy="82" r="3.5" fill="#6366f1" opacity="0.9"
                          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 3 }} />
                        
                        {/* Extended Nodes */}
                        <motion.circle cx="50" cy="5" r="2.5" fill="#3b82f6" opacity="0.8"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} />
                        <motion.circle cx="5" cy="50" r="2.5" fill="#6366f1" opacity="0.8"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }} />
                        <motion.circle cx="95" cy="50" r="2.5" fill="#4f46e5" opacity="0.8"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2.2 }} />
                        <motion.circle cx="50" cy="95" r="2.5" fill="#3b82f6" opacity="0.8"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 3.2 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="25" fill="url(#gradient9)"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="16" fill="url(#gradient10)"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.1 }} />
                        <motion.circle cx="50" cy="50" r="7" fill="#ffffff"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} />
                        
                        {/* Connections */}
                        <motion.line x1="18" y1="18" x2="50" y2="50" stroke="#6366f1" strokeWidth="0.9" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.9, 1.8, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="82" y1="18" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.9" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.9, 1.8, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
                        <motion.line x1="18" y1="82" x2="50" y2="50" stroke="#4f46e5" strokeWidth="0.9" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.9, 1.8, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }} />
                        <motion.line x1="82" y1="82" x2="50" y2="50" stroke="#6366f1" strokeWidth="0.9" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [0.9, 1.8, 0.9] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 3 }} />
                        
                        <defs>
                          <linearGradient id="gradient9" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="50%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#4f46e5" />
                          </linearGradient>
                          <linearGradient id="gradient10" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#4f46e5" />
                            <stop offset="100%" stopColor="#6366f1" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Item 6 */}
          <motion.div
            style={{ y, opacity }}
            className="relative flex items-center justify-center min-h-screen"
          >
            <div className="w-full max-w-6xl mx-auto px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Visual */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="relative lg:order-1"
                >
                  <div className="w-full h-96 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl shadow-2xl flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                      className="w-64 h-64"
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        {/* Neural Network Mesh - Teal Theme */}
                        <motion.circle cx="15" cy="15" r="4" fill="#14b8a6" opacity="0.9" 
                          animate={{ scale: [1, 1.7, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.circle cx="85" cy="15" r="4" fill="#10b981" opacity="0.9"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.1 }} />
                        <motion.circle cx="15" cy="85" r="4" fill="#059669" opacity="0.9"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2.2 }} />
                        <motion.circle cx="85" cy="85" r="4" fill="#14b8a6" opacity="0.9"
                          animate={{ scale: [1, 1.7, 1], opacity: [0.9, 1, 0.9] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 3.3 }} />
                        
                        {/* Outer Ring Nodes */}
                        <motion.circle cx="50" cy="3" r="3" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }} />
                        <motion.circle cx="3" cy="50" r="3" fill="#14b8a6" opacity="0.8"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.3 }} />
                        <motion.circle cx="97" cy="50" r="3" fill="#059669" opacity="0.8"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2.5 }} />
                        <motion.circle cx="50" cy="97" r="3" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 3.7 }} />
                        
                        {/* Central Core */}
                        <motion.circle cx="50" cy="50" r="28" fill="url(#gradient11)"
                          animate={{ scale: [1, 1.35, 1], opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.circle cx="50" cy="50" r="18" fill="url(#gradient12)"
                          animate={{ scale: [1, 1.45, 1], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }} />
                        <motion.circle cx="50" cy="50" r="8" fill="#ffffff"
                          animate={{ scale: [1, 1.8, 1], opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} />
                        
                        {/* Connections */}
                        <motion.line x1="15" y1="15" x2="50" y2="50" stroke="#14b8a6" strokeWidth="1" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [1, 2, 1] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0 }} />
                        <motion.line x1="85" y1="15" x2="50" y2="50" stroke="#10b981" strokeWidth="1" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [1, 2, 1] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.1 }} />
                        <motion.line x1="15" y1="85" x2="50" y2="50" stroke="#059669" strokeWidth="1" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [1, 2, 1] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2.2 }} />
                        <motion.line x1="85" y1="85" x2="50" y2="50" stroke="#14b8a6" strokeWidth="1" opacity="0.9"
                          animate={{ opacity: [0.9, 1, 0.9], strokeWidth: [1, 2, 1] }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 3.3 }} />
                        
                        <defs>
                          <linearGradient id="gradient11" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#14b8a6" />
                            <stop offset="50%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#059669" />
                          </linearGradient>
                          <linearGradient id="gradient12" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="50%" stopColor="#059669" />
                            <stop offset="100%" stopColor="#14b8a6" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="space-y-6 lg:order-2"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">06</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-slim">Client Follow-up Agent</h3>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed font-slim">
                    An AI Client Follow-Up Agent is an intelligent automation system designed to track, engage, and re-contact clients or leads automatically after key interactions — such as inquiries, meetings, purchases, or missed communications.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm border border-yellow-400/30">Auto Messaging</span>
                    <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm border border-cyan-400/30">Lead Nurture</span>
                    <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm border border-green-400/30">Response Tracking</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Gradient Separation Line */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-b from-transparent via-oklch(0.23 0.002 264) to-oklch(0.23 0.002 264)"></div>
    </div>
  );
};

export default ProductPage;